import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Wallet, History, QrCode, Grid3X3, User } from "lucide-react";
import { useApp } from "@/App";
import { cn } from "@/lib/utils";
import { useQrMaintenance } from "@/lib/qrMaintenance";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const navItems = [
  { path: "/", icon: Wallet, labelKey: "nav.wallet" },
  { path: "/services", icon: Grid3X3, labelKey: "nav.services" },
  { path: "/scan", icon: QrCode, labelKey: "", isMain: true },
  { path: "/history", icon: History, labelKey: "nav.history" },
  { path: "/profile", icon: User, labelKey: "nav.profile" },
];

export const BottomNav = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { haptic } = useApp();
  const qrMaintenance = useQrMaintenance();
  const [isVisible, setIsVisible] = useState(true);
  const tgPlatform = typeof window !== "undefined"
    ? String(window.Telegram?.WebApp?.platform || "").toLowerCase()
    : "";
  const navPlatform = tgPlatform.includes("android")
    ? "android"
    : tgPlatform.includes("ios")
      ? "ios"
      : "other";

  const toHexColor = (input) => {
    const color = String(input || "");
    const hexMatch = color.match(/^#([0-9a-f]{6}|[0-9a-f]{3})$/i);
    if (hexMatch) {
      const raw = hexMatch[1];
      if (raw.length === 3) {
        return `#${raw.split("").map((ch) => `${ch}${ch}`).join("")}`.toLowerCase();
      }
      return `#${raw.toLowerCase()}`;
    }

    const rgbMatch = color.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (!rgbMatch) return "";
    const [r, g, b] = rgbMatch.slice(1, 4).map((v) => Number(v));
    if (![r, g, b].every((v) => Number.isFinite(v) && v >= 0 && v <= 255)) return "";
    return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
  };

  useEffect(() => {
    // Detect platform
    const platform = window.Telegram?.WebApp?.platform || 'unknown';
    const isMobile = ['android', 'ios', 'android_x'].includes(platform) ||
      (platform === 'unknown' && window.innerHeight < 1200 && window.innerWidth < 800);

    if (!isMobile) {
      setIsVisible(true);
      return;
    }

    const initialHeight = window.innerHeight;

    const checkVisibility = () => {
      if (!window.visualViewport) return;

      const currentHeight = window.visualViewport.height;

      // If viewport shrunk by more than 15%, assume keyboard is up
      const isKeyboardUp = currentHeight < initialHeight * 0.85;

      // Also check if an input is explicitly focused as a secondary signal
      const activeElement = document.activeElement;
      const isInputFocused = activeElement && (
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement.tagName) ||
        activeElement.getAttribute('contenteditable') === 'true'
      );

      // Only hide if the keyboard is clearly up according to viewport OR input focused while small
      if (isKeyboardUp || (isInputFocused && currentHeight < initialHeight * 0.9)) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
    };

    window.visualViewport?.addEventListener('resize', checkVisibility);

    return () => {
      window.visualViewport?.removeEventListener('resize', checkVisibility);
    };
  }, []);

  useEffect(() => {
    if (!isVisible || navPlatform !== "ios") return;
    const tg = window.Telegram?.WebApp;
    if (!tg?.setBottomBarColor) return;

    const nav = document.querySelector(".bottom-nav");
    const navInner = document.querySelector(".bottom-nav-inner");
    if (!nav && !navInner) return;

    const navColorRaw = nav ? window.getComputedStyle(nav).backgroundColor : "";
    const navInnerColorRaw = navInner ? window.getComputedStyle(navInner).backgroundColor : "";
    const resolved = toHexColor(navColorRaw) || toHexColor(navInnerColorRaw);
    if (!resolved) return;

    const applyTelegramSurface = () => {
      try {
        tg.setBottomBarColor?.(resolved);
      } catch (_) {
        // Ignore Telegram bridge errors.
      }
    };

    try {
      tg.setBottomBarColor?.(resolved);
    } catch (_) {
      // Ignore Telegram bridge errors.
    }

    const rafId = window.requestAnimationFrame?.(() => {
      applyTelegramSurface();
    });
    const timerId = window.setTimeout(() => {
      applyTelegramSurface();
    }, 120);

    return () => {
      if (typeof rafId === "number" && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(rafId);
      }
      window.clearTimeout(timerId);
    };
  }, [isVisible, navPlatform, location.pathname]);

  if (!isVisible) return null;

  const handleNavigate = (item) => {
    haptic('light');
    // The scanner is the entry point of the QR payment flow: while maintenance
    // is on it explains itself instead of opening the camera.
    if (item.isMain && qrMaintenance.active) {
      toast.info(qrMaintenance.short);
      return;
    }
    navigate(item.path);
  };

  const isItemActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <nav className="bottom-nav" data-platform={navPlatform}>
      <div className="bottom-nav-inner lp-bar">
        <div className="nav-items">
          {navItems.map((item) => {
            const isActive = isItemActive(item.path);
            const Icon = item.icon;

            if (item.isMain) {
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigate(item)}
                  className={cn("qr-nav-btn", qrMaintenance.active && "opacity-50")}
                  aria-disabled={qrMaintenance.active}
                  data-testid="nav-scan-btn"
                >
                  <div className="qr-nav-btn-inner">
                    <Icon className="qr-nav-icon" />
                  </div>
                </button>
              );
            }

            return (
              <button
                key={item.path}
                onClick={() => handleNavigate(item)}
                className={cn("nav-item", isActive && "active")}
                data-testid={`nav-${item.labelKey.toLowerCase()}-btn`}
              >
                <div className="nav-item-content">
                  <Icon className="nav-icon" />
                  <span className="nav-label">{t(item.labelKey)}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default BottomNav;
